// =============================================================================
// ACT MATH TEST BANK
// Form 25MC2 (Official ACT Prep Guide 2025-2026, Practice Test 2)
// =============================================================================
//
// SOURCE: The Official ACT Prep Guide (2025-2026 Edition)
// TYPE: Practice Test (Enhanced ACT Format)
// USE: TESTING MODE
//
// FORMAT: Enhanced ACT
//   - 45 total questions, 41 scored
//   - 4 field test questions (likely Q6, Q18, Q30, Q38 based on answer key shading)
//   - 50 minutes
//
// =============================================================================

export const FORM_METADATA = {
  formId: '25MC2',
  source: 'Official ACT Prep Guide 2025-2026',
  testType: 'Practice Test',
  format: 'Enhanced',
  totalQuestions: 45,
  scoredQuestions: 41,
  fieldTestPositions: [6, 18, 30, 38],
  timeMinutes: 50
};

// Official Scale Score Conversion (Practice Test 2)
export const SCALE_SCORE_TABLE_25MC2 = [
  { raw: 41, scale: 36 }, { raw: 40, scale: 36 },
  { raw: 39, scale: 35 },
  { raw: 38, scale: 34 },
  { raw: 37, scale: 33 },
  { raw: 36, scale: 32 },
  { raw: 35, scale: 31 },
  { raw: 34, scale: 30 },
  { raw: 33, scale: 29 },
  { raw: 32, scale: 28 },
  { raw: 31, scale: 27 }, { raw: 30, scale: 27 },
  { raw: 29, scale: 26 },
  { raw: 28, scale: 25 }, { raw: 27, scale: 25 },
  { raw: 26, scale: 24 },
  { raw: 25, scale: 23 },
  { raw: 24, scale: 22 },
  { raw: 23, scale: 21 }, { raw: 22, scale: 21 },
  { raw: 21, scale: 20 },
  { raw: 20, scale: 19 },
  { raw: 19, scale: 18 }, { raw: 18, scale: 18 },
  { raw: 17, scale: 17 }, { raw: 16, scale: 17 }, { raw: 15, scale: 17 },
  { raw: 14, scale: 16 }, { raw: 13, scale: 16 }, { raw: 12, scale: 16 },
  { raw: 11, scale: 15 }, { raw: 10, scale: 15 },
  { raw: 9, scale: 14 }, { raw: 8, scale: 14 }, { raw: 7, scale: 14 },
  { raw: 6, scale: 13 },
  { raw: 5, scale: 12 },
  { raw: 4, scale: 11 },
  { raw: 3, scale: 10 },
  { raw: 2, scale: 8 },
  { raw: 1, scale: 5 },
  { raw: 0, scale: 1 }
];

// =============================================================================
// COMPLETE TEST BANK - FORM 25MC2 (45 Questions)
// =============================================================================

export const questions_25MC2 = [
  // -------------------------------------------------------------------------
  // QUESTION 1 - Simplifying algebraic expressions
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q01',
    formId: '25MC2',
    questionNumber: 1,
    categories: ['A'],
    difficulty: 1,
    topics: ['polynomial-simplification', 'exponents'],
    question: 'For all nonzero values of x and y, which of the following expressions is equivalent to −35x⁵y⁴/(5xy)?',
    choices: ['−7x⁴y³', '−7x⁵y⁴', '−7x⁶y⁵', '−40x⁴y³'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Divide coefficients: −35/5 = −7. Subtract exponents: x⁵/x = x⁴, y⁴/y = y³. Result: −7x⁴y³.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 2 - Triangle angle sum
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q02',
    formId: '25MC2',
    questionNumber: 2,
    categories: ['G'],
    difficulty: 1,
    topics: ['triangles', 'linear-equations'],
    question: 'The degree measures of the 3 angles of a triangle are (4x)°, (5x)°, and (6x)°. What is the value of x?',
    choices: ['10', '12', '24', '30'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Sum of angles = 180°. 4x + 5x + 6x = 180. 15x = 180. x = 12.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 3 - Division word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q03',
    formId: '25MC2',
    questionNumber: 3,
    categories: ['IES'],
    difficulty: 1,
    topics: ['decimals', 'word-problems'],
    question: 'A 48.5-ounce batch of cologne will be used to fill empty bottles. Each full bottle will contain 0.35 ounces of cologne. This batch of cologne will fill at most how many bottles full of cologne?',
    choices: ['13', '14', '138', '139'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: '48.5 ÷ 0.35 = 138.57. Can only fill complete bottles, so 138.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 4 - Percent word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q04',
    formId: '25MC2',
    questionNumber: 4,
    categories: ['IES'],
    difficulty: 2,
    topics: ['percents', 'word-problems', 'multi-step'],
    question: 'Of the 200 parking spaces in a parking lot, 6% are reserved for accessible parking. Of those NOT reserved for accessible parking, 20 are suitable for compact cars only. How many spaces are NOT reserved for accessible parking and suitable for noncompact cars?',
    choices: ['160', '168', '180', '188'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Accessible: 200 × 0.06 = 12. Not accessible: 200 − 12 = 188. Noncompact: 188 − 20 = 168.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 5 - Arithmetic sequence word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q05',
    formId: '25MC2',
    questionNumber: 5,
    categories: ['F'],
    difficulty: 2,
    topics: ['arithmetic-sequences', 'word-problems'],
    question: 'Ricardo started a savings account for his daughter Ruth by depositing $500 for her 1st birthday. For each successive birthday, Ricardo deposits $200 more than the previous birthday. What is the total deposited by her 6th birthday?',
    choices: ['$4,000', '$4,200', '$4,700', '$6,000'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Deposits: $500, $700, $900, $1100, $1300, $1500. Sum = 500 + 700 + 900 + 1100 + 1300 + 1500 = $6,000.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 6 - Matrix addition (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q06',
    formId: '25MC2',
    questionNumber: 6,
    categories: ['N'],
    difficulty: 1,
    topics: ['matrices'],
    question: 'Which of the following matrices is equal to [9,2; −4,1] + [−6,8; 7,6]?',
    choices: ['[−40, 84; 31, −26]', '[3, 10; 3, 7]', '[3, 10; 11, 7]', '[11, 2; −3, 13]'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'Add corresponding elements: [9+(−6), 2+8; −4+7, 1+6] = [3, 10; 3, 7].',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 7 - Fraction subtraction word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q07',
    formId: '25MC2',
    questionNumber: 7,
    categories: ['IES'],
    difficulty: 1,
    topics: ['fractions', 'word-problems'],
    question: 'Lyle and Ming are painting an art room. They started with 4 gallons of paint. On the first day, Lyle used ½ gallon and Ming used 1¼ gallons. How many gallons were left?',
    choices: ['1¾', '2¼', '2¾', '3½'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Used: ½ + 1¼ = ½ + 5/4 = 2/4 + 5/4 = 7/4 = 1¾. Left: 4 − 1¾ = 2¼.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 8 - Slope of a line
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q08',
    formId: '25MC2',
    questionNumber: 8,
    categories: ['G'],
    difficulty: 1,
    topics: ['slope', 'coordinate-geometry'],
    question: 'In the standard (x,y) coordinate plane, what is the slope of the line through (−3,1) and (5,6)?',
    choices: ['−5/2', '−5/8', '5/8', '5/2'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Slope = (6−1)/(5−(−3)) = 5/8.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 9 - Similar triangles
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q09',
    formId: '25MC2',
    questionNumber: 9,
    categories: ['G'],
    difficulty: 2,
    topics: ['similar-triangles', 'proportions'],
    question: 'The lengths of corresponding sides of 2 similar right triangles are in the ratio 4:5. The hypotenuse of the smaller triangle is 24 inches. How many inches long is the hypotenuse of the larger triangle?',
    choices: ['1.25', '20', '25', '30'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: '4/5 = 24/x. Cross multiply: 4x = 120. x = 30.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 10 - Sine ratio
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q10',
    formId: '25MC2',
    questionNumber: 10,
    categories: ['G'],
    difficulty: 1,
    topics: ['trig-ratios', 'triangles'],
    question: 'The lengths of the 3 sides of right triangle △ABC are given in meters. What is sin A?',
    choices: ['8/17', '15/17', '17/15', '17/8'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'In the figure, side opposite A is 8, hypotenuse is 17. sin A = opposite/hypotenuse = 8/17.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 11 - Distance in coordinate geometry
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q11',
    formId: '25MC2',
    questionNumber: 11,
    categories: ['G'],
    difficulty: 3,
    topics: ['distance-formula', 'pythagorean-theorem'],
    question: 'C is on segment AD. Distance AB = 2,000 km, AC = 1,600 km, AD = 2,500 km, BC = 1,200 km. What is the distance BD in kilometers?',
    choices: ['300√7', '900', '1,200', '1,500'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'CD = AD − AC = 2500 − 1600 = 900. Using the given diagram with B off the line, calculate BD using triangle relationships. BD = 1,500 km.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 12 - Sum of roots (Vieta's formulas)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q12',
    formId: '25MC2',
    questionNumber: 12,
    categories: ['A'],
    difficulty: 2,
    topics: ['quadratic-equations', 'factoring'],
    question: 'What is the sum of the 2 solutions of the equation x² − 4x − 45 = 0?',
    choices: ['−45', '−5', '0', '4'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'By Vieta\'s formulas, sum of roots = −b/a = −(−4)/1 = 4. Or factor: (x−9)(x+5)=0, roots are 9 and −5, sum = 4.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 13 - Coordinate movement
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q13',
    formId: '25MC2',
    questionNumber: 13,
    categories: ['G'],
    difficulty: 2,
    topics: ['coordinate-geometry', 'rate-problems'],
    question: 'A blinking dot is at (2,3). It moves horizontally +x direction for 4 seconds at 0.5 units/sec, then vertically +y for 2 seconds at same speed. Final position?',
    choices: ['(2,6)', '(3,5)', '(4,4)', '(5,3)'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Horizontal: 4 × 0.5 = 2 units right. (2,3) → (4,3). Vertical: 2 × 0.5 = 1 unit up. (4,3) → (4,4).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 14 - Negative exponent equation
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q14',
    formId: '25MC2',
    questionNumber: 14,
    categories: ['A'],
    difficulty: 2,
    topics: ['exponents', 'solving-equations'],
    question: 'What value of x makes the equation −1/81 = −3ˣ true?',
    choices: ['−4', '4', '27', '1/243'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: '−1/81 = −3ˣ. So 1/81 = 3ˣ. 81 = 3⁴, so 1/81 = 3⁻⁴. x = −4.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 15 - Solving for variable
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q15',
    formId: '25MC2',
    questionNumber: 15,
    categories: ['A'],
    difficulty: 2,
    topics: ['solving-for-variable', 'radicals'],
    question: 'The equation R = P/I² gives resistance R in terms of power P and current I. Which expression gives I in terms of P and R?',
    choices: ['√(R/P)', '√(P/R)', 'P/R', 'R/P'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'R = P/I². I² = P/R. I = √(P/R).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 16 - LCM word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q16',
    formId: '25MC2',
    questionNumber: 16,
    categories: ['N'],
    difficulty: 2,
    topics: ['gcd-lcm', 'word-problems'],
    question: 'One welcome sign flashes every 8 seconds, another every 12 seconds. At a certain instant, they flash together. How many seconds until they next flash together?',
    choices: ['4', '20', '24', '96'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'LCM(8, 12) = 24 seconds.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 17 - Square perimeter from area
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q17',
    formId: '25MC2',
    questionNumber: 17,
    categories: ['G'],
    difficulty: 1,
    topics: ['rectangles', 'area'],
    question: 'The area of a certain square is 900 square inches. What is the perimeter of this square in inches?',
    choices: ['30', '60', '120', '225'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Side = √900 = 30. Perimeter = 4 × 30 = 120.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 18 - Capture-recapture (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q18',
    formId: '25MC2',
    questionNumber: 18,
    categories: ['S'],
    difficulty: 2,
    topics: ['proportions', 'word-problems'],
    question: 'DNR tagged 108 deer. Later captured 54 deer, 36 were tagged. Using proportion (tagged in capture 1)/(population) = (tagged in capture 2)/(deer in capture 2), estimate population.',
    choices: ['108', '126', '144', '162'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: '108/P = 36/54. 108 × 54 = 36P. P = 5832/36 = 162.',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 19 - Arithmetic sequence
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q19',
    formId: '25MC2',
    questionNumber: 19,
    categories: ['F'],
    difficulty: 2,
    topics: ['arithmetic-sequences'],
    question: 'In an arithmetic sequence, the 10th term (a₁₀) is 30, and the common difference is 2. What is the 1st term (a₁)?',
    choices: ['6', '11', '12', '15'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'aₙ = a₁ + (n−1)d. 30 = a₁ + 9(2). 30 = a₁ + 18. a₁ = 12.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 20 - Unit conversion
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q20',
    formId: '25MC2',
    questionNumber: 20,
    categories: ['IES'],
    difficulty: 2,
    topics: ['unit-conversion', 'word-problems'],
    question: 'A pedometer records 3,898 steps for a person covering 2.25 feet per step. How many miles to the nearest 0.1 mile? (1 mile = 5,280 feet)',
    choices: ['0.3', '0.6', '1.7', '3.0'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Distance = 3898 × 2.25 = 8770.5 feet. Miles = 8770.5/5280 ≈ 1.66 ≈ 1.7 miles.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 21 - Venn diagram / Set theory
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q21',
    formId: '25MC2',
    questionNumber: 21,
    categories: ['S'],
    difficulty: 2,
    topics: ['venn-diagrams', 'sets'],
    question: 'Among 20 students, 13 are in Math Club, 11 are in Drama Club, and 9 are in both. How many are NOT members of either club?',
    choices: ['4', '5', '11', '13'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'In either = 13 + 11 − 9 = 15. Not in either = 20 − 15 = 5.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 22 - Mean and sum relationship
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q22',
    formId: '25MC2',
    questionNumber: 22,
    categories: ['S'],
    difficulty: 2,
    topics: ['mean', 'word-problems'],
    question: 'For the mean of 7 numbers to increase by 4, by how much would the sum of the 7 numbers have to increase?',
    choices: ['4', '7', '11', '28'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Mean = Sum/7. If mean increases by 4, sum must increase by 4 × 7 = 28.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 23 - Circle equation
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q23',
    formId: '25MC2',
    questionNumber: 23,
    categories: ['G'],
    difficulty: 2,
    topics: ['circles', 'coordinate-geometry'],
    question: 'Which is an equation of the circle with center (4,−3) and radius 5 coordinate units?',
    choices: ['(x−4)² + (y+3)² = 25', '(x−3)² + (y+4)² = 25', '(x+3)² + (y−4)² = 5', '(x+4)² + (y−3)² = 25'],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'Circle: (x−h)² + (y−k)² = r². Center (4,−3), r=5: (x−4)² + (y−(−3))² = 25 → (x−4)² + (y+3)² = 25.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 24 - Median
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q24',
    formId: '25MC2',
    questionNumber: 24,
    categories: ['S'],
    difficulty: 2,
    topics: ['median'],
    question: 'All 25 students in a chemistry class took a test. Each earned an integer score, no two the same. The median was 80. How many students scored greater than 80?',
    choices: ['12', '13', '14', '20'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'With 25 students, median is the 13th value. 12 below, median at position 13, 12 above. Answer: 12.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 25 - System of equations word problem
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q25',
    formId: '25MC2',
    questionNumber: 25,
    categories: ['A'],
    difficulty: 3,
    topics: ['systems-of-equations', 'word-problems'],
    question: 'Two Saturdays ago, 3 apples + 4 oranges = $3.47. Last Saturday, 12 oranges = $6.36. With $10, maximum apples today? (Prices unchanged, no tax)',
    choices: ['19', '21', '22', '23'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Orange price: $6.36/12 = $0.53. Apple: (3.47 − 4×0.53)/3 = (3.47 − 2.12)/3 = 1.35/3 = $0.45. Max apples: floor(10/0.45) = 22.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 26 - Right triangle trigonometry
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q26',
    formId: '25MC2',
    questionNumber: 26,
    categories: ['G'],
    difficulty: 2,
    topics: ['trig-ratios', 'triangles'],
    question: 'In △DEF, EG is an altitude, ∠DEF is a right angle, EF = 20 cm, and ∠EDF = 30°. What is EG in centimeters?',
    choices: ['10', '10√3', '20', '20√3'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'In right triangle DEF with 30° at D, EF = 20 (opposite to 30°). sin 30° = EG/DE and other relationships give EG = 10√3.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 27 - Function operations
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q27',
    formId: '25MC2',
    questionNumber: 27,
    categories: ['F'],
    difficulty: 2,
    topics: ['function-evaluation', 'polynomial-simplification'],
    question: 'Let f(x) = x² + 7x − 3 and g(x) = x² − 4x + 5. Let h(x) = f(x) − g(x). What is h(2)?',
    choices: ['8', '14', '22', '24'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'h(x) = (x² + 7x − 3) − (x² − 4x + 5) = 11x − 8. h(2) = 11(2) − 8 = 22 − 8 = 14.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 28 - Expression with constraints
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q28',
    formId: '25MC2',
    questionNumber: 28,
    categories: ['A'],
    difficulty: 3,
    topics: ['inequalities', 'rational-expressions'],
    question: 'For real numbers x and y such that 0 ≤ x ≤ 5 and y ≥ 9, the expression (x+y)/y can have which of the following values?',
    choices: ['6', '14/5', '10/9', '0'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '(x+y)/y = x/y + 1. With x ≤ 5 and y ≥ 9, x/y ≤ 5/9, so max ≈ 1.56. When x=1, y=9: (1+9)/9 = 10/9 ≈ 1.11. This is achievable.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 29 - Composite function
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q29',
    formId: '25MC2',
    questionNumber: 29,
    categories: ['F'],
    difficulty: 2,
    topics: ['composite-functions'],
    question: 'Given f(x) = x² + 1 and g(x) = x − 3, which expression is f(g(x))?',
    choices: ['x² − 8', 'x² − 6x + 10', 'x² + x − 2', 'x³ − 3x² + x − 3'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'f(g(x)) = f(x−3) = (x−3)² + 1 = x² − 6x + 9 + 1 = x² − 6x + 10.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 30 - Ratio from equation (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q30',
    formId: '25MC2',
    questionNumber: 30,
    categories: ['A'],
    difficulty: 2,
    topics: ['ratios', 'solving-for-variable'],
    question: 'Given that the equation (4x−y)/(x+y) = 5/2 is true, what is the value of x/y?',
    choices: ['2/3', '5/2', '7/3', '7/5'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Cross multiply: 2(4x−y) = 5(x+y). 8x − 2y = 5x + 5y. 3x = 7y. x/y = 7/3.',
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 31 - Graph interpretation (average speed)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q31',
    formId: '25MC2',
    questionNumber: 31,
    categories: ['F'],
    difficulty: 2,
    topics: ['rate-problems', 'graph-interpretation'],
    question: 'Juro traveled to 3 locations during a workday. Graph shows distance vs time. Which value is closest to the average speed (km/hr) when Juro was traveling?',
    choices: ['45', '57', '60', '75'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'From graph, traveled ~450 km in ~6 hours of actual movement. Average speed = 450/6 = 75 km/hr.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 32 - Amplitude of sine function
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q32',
    formId: '25MC2',
    questionNumber: 32,
    categories: ['F'],
    difficulty: 1,
    topics: ['trig-functions', 'amplitude'],
    question: 'What is the amplitude of the function y = 3 sin x?',
    choices: ['1/3', '1', '3', '6'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'For y = A sin x, amplitude = |A| = |3| = 3.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 33 - Adding rational expressions
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q33',
    formId: '25MC2',
    questionNumber: 33,
    categories: ['A'],
    difficulty: 2,
    topics: ['rational-expressions'],
    question: 'For all nonzero values of w, which expression is equivalent to 4/w + 2/w²?',
    choices: ['(2w+1)/w²', '(4w+2)/w²', '6/(w+w²)', '6/w²'],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: '4/w + 2/w² = 4w/w² + 2/w² = (4w+2)/w².',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 34 - Number line and product
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q34',
    formId: '25MC2',
    questionNumber: 34,
    categories: ['N'],
    difficulty: 2,
    topics: ['number-properties', 'inequalities'],
    question: 'A number line shows points a (between −1 and 0) and b (between 0 and 1). Which inequality must include the product ab?',
    choices: ['−1 < ab < a', 'a < ab < 0', '0 < ab < b', 'b < ab < 1'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'a is negative, b is positive (0 < b < 1), so ab is negative. Also |ab| < |a| since 0 < b < 1, so a < ab < 0.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 35 - Weighted average
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q35',
    formId: '25MC2',
    questionNumber: 35,
    categories: ['S'],
    difficulty: 2,
    topics: ['mean', 'word-problems'],
    question: 'Sani\'s grade: 3 tests at 20% each (scores: 78, 86, 82) and final exam at 40%. What minimum final exam score for course grade of at least 86?',
    choices: ['84', '90', '92', '98'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Test avg contribution: 0.2(78) + 0.2(86) + 0.2(82) = 49.2. Need: 49.2 + 0.4x ≥ 86. 0.4x ≥ 36.8. x ≥ 92.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 36 - Triangle area from vertices
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q36',
    formId: '25MC2',
    questionNumber: 36,
    categories: ['G'],
    difficulty: 2,
    topics: ['coordinate-geometry', 'triangles', 'area'],
    question: 'Triangle has vertices A(−1,−2), B(2,2), and C(−1,4). What is the area in square coordinate units?',
    choices: ['9', '15', '18', '3√13'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Base AC is vertical with length |4−(−2)| = 6. Height from B to line x=−1 is |2−(−1)| = 3. Area = ½(6)(3) = 9.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 37 - Square root property
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q37',
    formId: '25MC2',
    questionNumber: 37,
    categories: ['N'],
    difficulty: 2,
    topics: ['radicals', 'number-properties'],
    question: 'For some real number x, √x² ≠ x. Therefore, x is:',
    choices: ['greater than π', 'irrational', 'negative', 'undefined'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: '√x² = |x|. If √x² ≠ x, then |x| ≠ x, which means x is negative.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 38 - Point dividing segment (FIELD TEST)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q38',
    formId: '25MC2',
    questionNumber: 38,
    categories: ['G'],
    difficulty: 3,
    topics: ['coordinate-geometry', 'midpoint'],
    question: 'Point A is (2,−4), point B(8,−1) divides AC so that AB:BC = 1:3. What are coordinates of C?',
    choices: ['(32,11)', '(26,8)', '(14,2)', '(10,0)'],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'B divides AC in ratio 1:3, so B is 1/4 of way from A to C. C = A + 4(B−A) = (2,−4) + 4(6,3) = (2+24, −4+12) = (26,8).',
    hasFigure: true,
    isScored: false  // Field test question
  },

  // -------------------------------------------------------------------------
  // QUESTION 39 - Wire around posts
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q39',
    formId: '25MC2',
    questionNumber: 39,
    categories: ['G'],
    difficulty: 3,
    topics: ['circles', 'perimeter'],
    question: 'A wire binds 4 identical posts together (each with 3-inch radius). What is the length, to the nearest inch, of the shortest wire that will go around the 4 posts without overlap?',
    choices: ['31', '33', '43', '52'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Wire consists of 4 straight segments (each 6 in = 2 diameters) between tangent points, plus 4 quarter circles. Length = 4(6) + 4(¼)(2π×3) = 24 + 6π ≈ 24 + 18.85 ≈ 43.',
    hasFigure: true,
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 40 - Complex number conjugate
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q40',
    formId: '25MC2',
    questionNumber: 40,
    categories: ['N'],
    difficulty: 2,
    topics: ['complex-numbers'],
    question: 'Given that b is rational and i = √−1, the product of (3 + bi) and which expression must be a rational number?',
    choices: ['bi', '3bi', '3 + bi', '3 − bi'],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: '(3+bi)(3−bi) = 9 − b²i² = 9 − b²(−1) = 9 + b², which is rational since b is rational.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 41 - Logarithm
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q41',
    formId: '25MC2',
    questionNumber: 41,
    categories: ['F'],
    difficulty: 2,
    topics: ['logarithms'],
    question: 'For positive integers x and y where x < 8, logₓ 8 = y. What is the value of y?',
    choices: ['1', '2', '3', '4'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'xʸ = 8. With x < 8 and both positive integers: x=2, y=3 (since 2³=8).',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 42 - Fractional exponents
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q42',
    formId: '25MC2',
    questionNumber: 42,
    categories: ['N'],
    difficulty: 2,
    topics: ['exponents', 'radicals'],
    question: 'For all positive integers x, which expression is equivalent to x^(1/4) · x^(1/6)?',
    choices: ['⁵√x', '³√x¹²', '¹²√x⁵', '²⁴√x'],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'x^(1/4) · x^(1/6) = x^(1/4 + 1/6) = x^(3/12 + 2/12) = x^(5/12) = ¹²√x⁵.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 43 - Law of Cosines
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q43',
    formId: '25MC2',
    questionNumber: 43,
    categories: ['G'],
    difficulty: 3,
    topics: ['trig-ratios', 'triangles'],
    question: 'The sides of an acute triangle measure 17 cm, 16 cm, and 15 cm. The measure of the smallest angle is A. Which equation is a solution for A?',
    choices: ['17² = 15² + 16² − 2(15)(16)sin A', '17² = 15² + 16² − 2(15)(16)cos A', '15² = 16² + 17² − 2(16)(17)sin A', '15² = 16² + 17² − 2(16)(17)cos A'],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Smallest angle is opposite smallest side (15). Law of Cosines: c² = a² + b² − 2ab·cos C. So 15² = 16² + 17² − 2(16)(17)cos A.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 44 - Mean and range comparison
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q44',
    formId: '25MC2',
    questionNumber: 44,
    categories: ['S'],
    difficulty: 3,
    topics: ['mean', 'range'],
    question: 'Sets A and B each have 5 distinct numbers. They\'re identical except the smallest value. B\'s smallest > A\'s smallest. Which measure(s) must be greater for B than A?',
    choices: ['Mean only', 'Median only', 'Range only', 'Mean and range only'],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'If only smallest differs and B\'s is larger: Mean of B > Mean of A (sum is larger). Median unchanged (middle value same). Range of B < Range of A (smaller spread). Only mean is greater.',
    isScored: true
  },

  // -------------------------------------------------------------------------
  // QUESTION 45 - Probability (combinations)
  // -------------------------------------------------------------------------
  {
    id: '25MC2-Q45',
    formId: '25MC2',
    questionNumber: 45,
    categories: ['S'],
    difficulty: 3,
    topics: ['probability-basic', 'combinations'],
    question: 'Of 16 cars on a lot: 6 minivans, 7 sedans, 3 hatchbacks. Thalia rents 3 cars at random for business associates. What is the probability of getting 1 of each type?',
    choices: ['1/3', '3/16', '9/40', '9/80'],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'Total ways: C(16,3) = 560. Favorable: 6×7×3 = 126. Probability = 126/560 = 9/40.',
    isScored: true
  }
];

// =============================================================================
// CATEGORY SUMMARY
// =============================================================================

export const CATEGORY_COUNTS_25MC2 = {
  'A': 8,   // Algebra
  'F': 7,   // Functions
  'G': 11,  // Geometry
  'N': 5,   // Number & Quantity
  'S': 7,   // Statistics & Probability
  'IES': 7  // Integrating Essential Skills
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getScaleScore(rawScore) {
  const entry = SCALE_SCORE_TABLE_25MC2.find(e => e.raw === rawScore);
  if (entry) return entry.scale;
  // Handle ranges
  const rangeEntry = SCALE_SCORE_TABLE_25MC2.find(e => e.raw >= rawScore);
  return rangeEntry ? rangeEntry.scale : 1;
}

export function getQuestionsByCategory(categoryCode) {
  return questions_25MC2.filter(q => q.categories.includes(categoryCode));
}

export function getScoredQuestions() {
  return questions_25MC2.filter(q => q.isScored);
}

export function getFieldTestQuestions() {
  return questions_25MC2.filter(q => !q.isScored);
}

export default {
  metadata: FORM_METADATA,
  scaleTable: SCALE_SCORE_TABLE_25MC2,
  questions: questions_25MC2,
  categoryCounts: CATEGORY_COUNTS_25MC2,
  getScaleScore,
  getQuestionsByCategory,
  getScoredQuestions,
  getFieldTestQuestions
};
